// ═══════════════════════════════════════════════════════════════════════
// 🧪 CRUD Test Helper
// بيدعم نمطين للـ payload:
//   - "wrapped" → { data: [payload], token }   (مثل Benefit, Doctor, ICD...)
//   - "flat"    → { ...payload, token }          (مثل Policy, Patient)
// ═══════════════════════════════════════════════════════════════════════

export type PayloadStyle = "wrapped" | "flat";

export interface CrudTestConfig {
  pageName: string;        // مثلا "ICDNotCovered"
  axiosInstance: any;      // المثيل اللي تستخدمه
  hasSoftDelete?: boolean; // لو false → نتخطّى اختبارات softDelete/restore
  payloadStyle?: PayloadStyle; // "wrapped" (افتراضي) أو "flat"
  endpoints: {
    add: string;           // مثلا "addNewIcdNotCovered"
    update: string;        // مثلا "updateIcdNotCovered"
    fetch: string;         // مثلا "getIcdNotCovered"
    softDelete: string;    // مثلا "softDeleteIcdNotCovered/{id}"  (ممكن "" لو مافي)
    hardDelete: string;    // مثلا "deleteIcdNotCovered/{id}"
    restore: string;       // مثلا "restoreIcdNotCovered/{id}"     (ممكن "" لو مافي)
  };
}

export interface TestResult {
  testName: string;
  status: "pass" | "fail" | "skip";
  duration: number;
  error?: string | null;
  details?: any;
}

// ─── CRUD Test Suite ──────────────────────────────────────────────────────
export class CrudTestAutomation {
  private config: CrudTestConfig;
  private token: string;
  private results: TestResult[] = [];

  constructor(config: CrudTestConfig) {
    this.config = config;
    this.token = localStorage.getItem("token") || "";
  }

  // helper صغير: هل الصفحة بتدعم soft delete؟
  private get supportsSoftDelete(): boolean {
    return this.config.hasSoftDelete !== false
      && !!this.config.endpoints.softDelete
      && !!this.config.endpoints.restore;
  }

  // ✅ بيبني جسم الـ add حسب النمط
  private buildAddBody(payload: any): any {
    const style = this.config.payloadStyle || "wrapped";
    if (style === "flat") {
      // Policy/Patient: الحقول مباشرة + token (بدون data wrapper، وبدون array)
      const flat = Array.isArray(payload) ? payload[0] : payload;
      return { ...flat, token: this.token };
    }
    // wrapped (الافتراضي): { data: [payload], token }
    return { data: Array.isArray(payload) ? payload : [payload], token: this.token };
  }

  // ✅ بيبني جسم الـ update حسب النمط
  private buildUpdateBody(payload: any, id: string | number): any {
    const style = this.config.payloadStyle || "wrapped";
    if (style === "flat") {
      // Policy/Patient: الحقول مباشرة + id + token
      const flat = Array.isArray(payload) ? payload[0] : payload;
      return { ...flat, id, token: this.token };
    }
    // wrapped: { data: { ...payload, id }, token }
    return { data: { ...payload, id }, token: this.token };
  }

  // ─── ADD TEST ──────────────────────────────────────────────────────────
  async testAdd(payload: any, expectedFields?: string[]): Promise<TestResult> {
    const start = performance.now();
    const testName = `[${this.config.pageName}] Add Item`;

    try {
      const response = await this.config.axiosInstance.post(
        this.config.endpoints.add,
        this.buildAddBody(payload)
      );

      if (!response.data.success) {
        return this.recordResult(testName, "fail", start, response.data.error);
      }

      if (expectedFields && response.data.result) {
        const missingFields = expectedFields.filter(
          (f) => !Object.prototype.hasOwnProperty.call(response.data.result, f)
        );
        if (missingFields.length > 0) {
          return this.recordResult(
            testName,
            "fail",
            start,
            `Missing fields: ${missingFields.join(", ")}`
          );
        }
      }

      return this.recordResult(testName, "pass", start, null, {
        addedId: response.data.result?.id,
        timestamp: new Date().toISOString(),
      });
    } catch (error: any) {
      return this.recordResult(
        testName,
        "fail",
        start,
        error.response?.data?.error || error.message
      );
    }
  }

  // ─── UPDATE TEST ──────────────────────────────────────────────────────
  async testUpdate(id: string | number, updatedPayload: any): Promise<TestResult> {
    const start = performance.now();
    const testName = `[${this.config.pageName}] Update Item #${id}`;

    try {
      const response = await this.config.axiosInstance.put(
        this.config.endpoints.update,
        this.buildUpdateBody(updatedPayload, id)
      );

      if (!response.data.success) {
        return this.recordResult(testName, "fail", start, response.data.error);
      }

      return this.recordResult(testName, "pass", start, null, {
        updatedId: id,
        timestamp: new Date().toISOString(),
      });
    } catch (error: any) {
      return this.recordResult(
        testName,
        "fail",
        start,
        error.response?.data?.error || error.message
      );
    }
  }

  // ─── FETCH TEST ───────────────────────────────────────────────────────
  async testFetch(expectedMinItems: number = 0): Promise<TestResult> {
    const start = performance.now();
    const testName = `[${this.config.pageName}] Fetch Items`;

    try {
      const response = await this.config.axiosInstance.get(
        this.config.endpoints.fetch
      );

      const items = response.data.result || response.data || [];
      if (!Array.isArray(items)) {
        return this.recordResult(testName, "fail", start, "Response is not an array");
      }

      if (items.length < expectedMinItems) {
        return this.recordResult(
          testName,
          "fail",
          start,
          `Expected at least ${expectedMinItems} items, got ${items.length}`
        );
      }

      return this.recordResult(testName, "pass", start, null, {
        itemCount: items.length,
      });
    } catch (error: any) {
      return this.recordResult(
        testName,
        "fail",
        start,
        error.response?.data?.error || error.message
      );
    }
  }

  // ─── SOFT DELETE TEST ──────────────────────────────────────────────────
  async testSoftDelete(id: string | number): Promise<TestResult> {
    const start = performance.now();
    const testName = `[${this.config.pageName}] Soft Delete #${id}`;

    // ✅ تخطّي لو الصفحة ما بتدعم soft delete
    if (!this.supportsSoftDelete) {
      return this.recordResult(testName, "skip", start, null, { reason: "no soft delete endpoint" });
    }

    try {
      const response = await this.config.axiosInstance.delete(
        this.config.endpoints.softDelete.replace("{id}", String(id)),
        { data: { token: this.token } }
      );

      if (!response.data.success) {
        return this.recordResult(
          `${testName} (expected conflict)`,
          "pass",
          start,
          null,
          { requiresForce: true, error: response.data.error }
        );
      }

      return this.recordResult(testName, "pass", start);
    } catch (error: any) {
      return this.recordResult(
        testName,
        "fail",
        start,
        error.response?.data?.error || error.message
      );
    }
  }

  // ─── HARD DELETE TEST ──────────────────────────────────────────────────
  async testHardDelete(
    id: string | number,
    force: boolean = false
  ): Promise<TestResult> {
    const start = performance.now();
    const testName = `[${this.config.pageName}] Hard Delete #${id}${force ? " (forced)" : ""}`;

    try {
      const url = force
        ? this.config.endpoints.hardDelete.replace("{id}", String(id)) + "?force=true"
        : this.config.endpoints.hardDelete.replace("{id}", String(id));

      const response = await this.config.axiosInstance.delete(url, {
        data: { token: this.token },
      });

      if (!response.data.success) {
        return this.recordResult(testName, "fail", start, response.data.error);
      }

      return this.recordResult(testName, "pass", start);
    } catch (error: any) {
      return this.recordResult(
        testName,
        "fail",
        start,
        error.response?.data?.error || error.message
      );
    }
  }

  // ─── RESTORE TEST ──────────────────────────────────────────────────────
  async testRestore(id: string | number): Promise<TestResult> {
    const start = performance.now();
    const testName = `[${this.config.pageName}] Restore #${id}`;

    // ✅ تخطّي لو الصفحة ما بتدعم restore
    if (!this.supportsSoftDelete) {
      return this.recordResult(testName, "skip", start, null, { reason: "no restore endpoint" });
    }

    try {
      const response = await this.config.axiosInstance.patch(
        this.config.endpoints.restore.replace("{id}", String(id)),
        { token: this.token }
      );

      if (!response.data.success) {
        return this.recordResult(testName, "fail", start, response.data.error);
      }

      return this.recordResult(testName, "pass", start);
    } catch (error: any) {
      return this.recordResult(
        testName,
        "fail",
        start,
        error.response?.data?.error || error.message
      );
    }
  }

  // ─── VALIDATION TEST ──────────────────────────────────────────────────
  testValidateEntities(
    isActive: boolean[],
    hasData: boolean[]
  ): TestResult {
    const start = performance.now();
    const testName = `[${this.config.pageName}] Entity Validation`;

    try {
      // ✅ نسمّي حسب الـ index الأصلي قبل ما نفلتر
      const empty = isActive
        .map((active, i) => ({
          isActive: active,
          hasData: hasData[i],
          name: `Entity_${i}`,
        }))
        .filter((c) => c.isActive && !c.hasData)
        .map((c) => c.name);

      const shouldFail = empty.length >= 1;

      return this.recordResult(testName, "pass", start, null, {
        shouldFail,
        emptyEntities: empty,
      });
    } catch (error: any) {
      return this.recordResult(testName, "fail", start, error.message);
    }
  }

  // ─── BATCH TEST (Full CRUD Cycle) ──────────────────────────────────────
  async testFullCycle(addPayload: any, updatePayload: any): Promise<TestResult[]> {
    const cycleResults: TestResult[] = [];

    const addResult = await this.testAdd(addPayload);
    cycleResults.push(addResult);

    if (addResult.status !== "pass" || !addResult.details?.addedId) {
      return cycleResults;
    }

    const addedId = addResult.details.addedId;

    cycleResults.push(await this.testFetch(1));
    cycleResults.push(
      await this.testUpdate(addedId, { ...updatePayload, id: addedId })
    );
    cycleResults.push(await this.testSoftDelete(addedId)); // بيتخطّى تلقائياً لو مافي soft
    cycleResults.push(await this.testRestore(addedId));    // بيتخطّى تلقائياً لو مافي restore
    cycleResults.push(await this.testHardDelete(addedId));

    return cycleResults;
  }

  // ─── REPORT ───────────────────────────────────────────────────────────
  private recordResult(
    testName: string,
    status: "pass" | "fail" | "skip",
    start: number,
    error?: string | null,
    details?: any
  ): TestResult {
    const result: TestResult = {
      testName,
      status,
      duration: performance.now() - start,
      error,
      details,
    };

    this.results.push(result);
    return result;
  }

  printResults(): void {
    console.log("\n" + "═".repeat(80));
    console.log(`📊 QA AUTOMATION RESULTS - ${this.config.pageName}`);
    console.log("═".repeat(80));

    const passed = this.results.filter((r) => r.status === "pass").length;
    const failed = this.results.filter((r) => r.status === "fail").length;
    const skipped = this.results.filter((r) => r.status === "skip").length;

    this.results.forEach((result) => {
      const icon = result.status === "pass" ? "✅" : result.status === "skip" ? "⏭️" : "❌";
      const color = result.status === "pass" ? "\x1b[32m" : result.status === "skip" ? "\x1b[33m" : "\x1b[31m";
      const reset = "\x1b[0m";

      console.log(
        `${color}${icon} ${result.testName} (${result.duration.toFixed(2)}ms)${reset}`
      );
      if (result.error) {
        console.log(`   └─ ${result.error}`);
      }
      if (result.details) {
        console.log(`   └─ ${JSON.stringify(result.details)}`);
      }
    });

    console.log("═".repeat(80));
    console.log(`✅ Passed: ${passed} | ❌ Failed: ${failed} | ⏭️ Skipped: ${skipped} | Total: ${this.results.length}`);
    console.log("═".repeat(80) + "\n");
  }

  getResults(): TestResult[] {
    return this.results;
  }

  reset(): void {
    this.results = [];
  }
}